/**
 * "Authentication & access" overview — Auth section (ADR-0074).
 * The operational counterpart to the conceptual Core-concepts `identity` page:
 * how your team signs in, how your end users are authenticated, and the keys,
 * identity providers, and safeguards that connect them.
 */
import type { DocPageContent } from '../types';

export const AUTH_OVERVIEW_PAGE: DocPageContent = {
  slug: 'auth',
  titleKey: 'docs.auth.overview.title',
  descriptionKey: 'docs.auth.overview.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.auth.overview.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'two-audiences',
      textKey: 'docs.auth.overview.audiences.heading',
    },
    { kind: 'paragraph', textKey: 'docs.auth.overview.audiences.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.overview.audience.team.term',
          descKey: 'docs.auth.overview.audience.team.desc',
        },
        {
          termKey: 'docs.auth.overview.audience.endUser.term',
          descKey: 'docs.auth.overview.audience.endUser.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.auth.overview.concept.title',
      textKey: 'docs.auth.overview.concept.body',
      links: {
        concept: { target: { docSlug: 'identity' }, labelKey: 'docs.auth.overview.concept.link' },
      },
    },

    {
      kind: 'heading',
      level: 2,
      id: 'principles',
      textKey: 'docs.auth.overview.principles.heading',
    },
    { kind: 'paragraph', textKey: 'docs.auth.overview.principles.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.auth.overview.principle.actAs',
        'docs.auth.overview.principle.noCreds',
        'docs.auth.overview.principle.isolation',
      ],
    },

    { kind: 'heading', level: 2, id: 'explore', textKey: 'docs.auth.overview.explore.heading' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.auth.overview.card.signin.title',
          textKey: 'docs.auth.overview.card.signin.text',
          to: { docSlug: 'auth/sign-in' },
        },
        {
          titleKey: 'docs.auth.overview.card.endUser.title',
          textKey: 'docs.auth.overview.card.endUser.text',
          to: { docSlug: 'auth/end-user-auth' },
        },
        {
          titleKey: 'docs.auth.overview.card.providers.title',
          textKey: 'docs.auth.overview.card.providers.text',
          to: { docSlug: 'auth/identity-providers' },
        },
        {
          titleKey: 'docs.auth.overview.card.keys.title',
          textKey: 'docs.auth.overview.card.keys.text',
          to: { docSlug: 'auth/api-keys' },
        },
        {
          titleKey: 'docs.auth.overview.card.stepup.title',
          textKey: 'docs.auth.overview.card.stepup.text',
          to: { docSlug: 'auth/step-up' },
        },
        {
          titleKey: 'docs.auth.overview.card.team.title',
          textKey: 'docs.auth.overview.card.team.text',
          to: { docSlug: 'dashboard/team' },
        },
      ],
    },
  ],
};
